/**
 * Audio, centralised — because overlapping players are unfindable and unstoppable.
 *
 * The symptom this exists to kill: every clip used to create its own
 * `new Audio()` with autoplay, so browsing the queue stacked player after
 * player and "all the clips played together". Hot reloads made it worse by
 * orphaning elements nothing could reach.
 *
 * ---------------------------------------------------------------------------
 * WHAT THIS FILE GUARANTEES *TODAY* — read before trusting the rules below.
 * ---------------------------------------------------------------------------
 *
 * The three rules were written as if this module owned all playback. It does
 * not, and has not for some time. The honest statement is:
 *
 *   1. This module owns exactly TWO elements — `clipEl` (whole clips) and
 *      `previewEl` (the alignment modal's looping preview). Those two are
 *      reused forever and cannot accumulate. TRUE, and scoped to this file.
 *
 *      FOUR more elements live outside it, so SIX exist in the app:
 *        - `useClipPlayer.ts:57`      `new Audio()`
 *        - `useContextPlayer.ts:105`  `new Audio()`
 *        - `useRangePlayer.ts:70`     `new Audio()`
 *        - `CompletedPage.tsx:295`    JSX `<audio>` (the karaoke transcript)
 *
 *   2. "Starting either one pauses the other" holds only BETWEEN these two.
 *      `pauseAllAudio()` reaches 2 of the 6 — despite its name it cannot stop
 *      the three hooks or the Completed page, none of which register here.
 *
 *      The reachable consequence (follow-up F1, a real reproducible bug):
 *      on CorrectPage open "✂ Adjust cut", press ▶ inside the trimmer, then
 *      press the card's ▶ Play. Both sound at once. `toggleTrimmer` stops the
 *      clip and context players when the trimmer OPENS, but the guard is
 *      one-way — `handlePlay` and `handleContext` stop each other and never
 *      the trimmer's `useRangePlayer`.
 *
 *   3. "Nothing plays until a human asks" — TRUE everywhere, including the four
 *      outsiders. No autoplay was ever reintroduced. This is the one rule that
 *      still holds app-wide.
 *
 * THE FIX (F1, deliberately not done in the restructure pass, because making
 * two players stop each other changes behavior): have the three hooks and
 * CompletedPage register their element here, and have every `play()` call
 * `pauseAllAudio()` first. Until then, treat rule 2 as a property of this
 * file, not of the app.
 *
 * Layer note: this is the L3 audio surface. `services/` must never import it
 * (rule R5) — a pipeline that stops the user's playback is not a pipeline.
 */

let clipEl: HTMLAudioElement | null = null;
let previewEl: HTMLAudioElement | null = null;
let clipUrl: string | null = null;

function ensure(which: 'clip' | 'preview'): HTMLAudioElement {
  if (which === 'clip') {
    if (!clipEl) clipEl = new Audio();
    return clipEl;
  }
  if (!previewEl) previewEl = new Audio();
  return previewEl;
}

/** The one element used to play whole clips. */
export function clipPlayer(): HTMLAudioElement {
  return ensure('clip');
}

/** The one element used by the alignment modal to loop a selection. */
export function previewPlayer(): HTMLAudioElement {
  return ensure('preview');
}

/** Stop everything this app is playing. */
export function pauseAllAudio(): void {
  clipEl?.pause();
  previewEl?.pause();
}

/**
 * Point the clip player at a blob and play it (looping), silencing the other
 * player first. The previous blob URL is revoked — one URL alive at a time.
 */
export function playClipBlob(blob: Blob, loop = true): HTMLAudioElement {
  const el = clipPlayer();
  previewEl?.pause();
  el.pause();
  if (clipUrl) URL.revokeObjectURL(clipUrl);
  clipUrl = URL.createObjectURL(blob);
  el.src = clipUrl;
  el.loop = loop;
  el.currentTime = 0;
  void el.play().catch(() => undefined);
  return el;
}

/** Release the clip player's source (leaving a screen). */
export function releaseClip(): void {
  clipEl?.pause();
  if (clipEl) clipEl.src = '';
  if (clipUrl) URL.revokeObjectURL(clipUrl);
  clipUrl = null;
}

/** Start the preview player, silencing the clip player first. */
export function playPreview(): void {
  clipEl?.pause();
  const el = previewPlayer();
  void el.play().catch(() => undefined);
}
